// Rejections — PRs the agent did NOT adopt / review, with the reason. Surfaces
// the PrAdoptionLedger + PrReviewLedger rows that don't end in a successful
// verdict, so silently-dropped PRs become visible & operator-actionable. List
// is server-paginated; filter / search / time window all map to query params.

import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../api";
import useBssPoll from "../../hooks/useBssPoll";
import Icon from "./icons";
import Shell from "./Shell";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  RefreshButton,
  apiErrorMessage,
  relativeTime
} from "./shared";

const KIND_FILTERS = [
  ["all", "All"],
  ["adoption", "Adoption"],
  ["review", "Review"]
];

const WINDOWS = [
  [24, "24h"],
  [24 * 7, "7d"],
  [24 * 30, "30d"]
];

const PAGE_SIZE = 25;

// Lightly humanize verdicts for the badge. Untouched verdicts (anything we
// haven't explicitly mapped) fall through verbatim — never invent a label.
const VERDICT_LABEL = {
  rejected_classifier: "not CRM-relevant",
  rejected_no_ticket: "no ticket key",
  rejected_already_owned: "already owned",
  rejected_jira_404: "Jira 404",
  skipped_not_relevant: "not CRM-relevant",
  skipped_no_ticket: "no ticket key",
  skipped_jira_404: "Jira 404",
  skipped_agent_pr: "agent-authored",
  review_loop_guard: "review loop guard",
  failed: "failed"
};
const verdictLabel = (v) => VERDICT_LABEL[v] || v;

// Most verdicts are operational drops (gray). Hard failures lean red so they
// stand out at a glance in a long list.
const verdictBadgeClass = (v) =>
  v === "failed" ? "bss-badge bss-badge--red" : "bss-badge bss-badge--gray";

// Server returns the full "owner/repo" (`githubRepo`) from the registry so
// the UI doesn't bake in an org convention. Fall back to "boompay/<repoId>"
// only if an old row predates the resolver — better than a broken link.
const prUrl = (row) => {
  const repo = row.githubRepo || `boompay/${row.repoId}`;
  return `https://github.com/${repo}/pull/${row.prNumber}`;
};

export default function RejectionsPage() {
  const [kind, setKind] = useState("all");
  const [windowHours, setWindowHours] = useState(24 * 7);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [page, setPage] = useState(0);

  // Debounce the search box so each keystroke doesn't fire a request.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  // Reset to first page when any of these change.
  useEffect(() => {
    setPage(0);
  }, [kind, windowHours, debouncedQuery]);

  const fetchRejections = useCallback(
    () =>
      api
        .bssRejections({
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
          kind: kind === "all" ? undefined : kind,
          sinceHours: windowHours,
          query: debouncedQuery || undefined
        })
        .then((res) => res.data),
    [page, kind, windowHours, debouncedQuery]
  );
  const { data, error, loading, stale, refresh } = useBssPoll(
    fetchRejections,
    { deps: [page, kind, windowHours, debouncedQuery] }
  );

  const items = useMemo(() => (data && data.items) || [], [data]);
  const total = (data && data.total) || 0;
  // SS sets `truncated: true` when either ledger exceeds the per-ledger fetch
  // cap (2000 rows in the window). Past that point page ordering can miss
  // rows, so we surface a hint to narrow the window rather than page deeper.
  const truncated = !!(data && data.truncated);
  const offset = page * PAGE_SIZE;

  // Toolbar stays mounted across refetches so the search input doesn't
  // flash on each keystroke (previously the whole body swapped to
  // <LoadingState />, destroying the input). Stale-while-revalidate: keep
  // the previous results visible during refetch.
  const toolbar = (
    <div className="bss-toolbar">
      <div className="bss-filter-tabs">
        {KIND_FILTERS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`bss-filter-tab ${
              kind === key ? "is-active" : ""
            }`.trim()}
            onClick={() => setKind(key)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="bss-filter-tabs" style={{ marginLeft: 8 }}>
        {WINDOWS.map(([h, label]) => (
          <button
            key={h}
            type="button"
            className={`bss-filter-tab ${
              windowHours === h ? "is-active" : ""
            }`.trim()}
            onClick={() => setWindowHours(h)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="bss-search-wrap" style={{ flex: 1, marginLeft: 8 }}>
        <Icon.Search size={14} className="bss-icon" />
        <input
          className="bss-input"
          placeholder="Filter by repo, PR #, verdict, or reason…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
    </div>
  );

  let body;
  if (loading && !data) body = <LoadingState label="Loading rejections…" />;
  else if (error && !data)
    body = <ErrorState message={apiErrorMessage(error)} onRetry={refresh} />;
  else
    body = (
      <>
        <div className="bss-card" style={{ overflow: "hidden" }}>
          <table className="bss-table">
            <thead>
              <tr>
                <th style={{ width: 90 }}>Kind</th>
                <th style={{ width: 220 }}>Repo</th>
                <th style={{ width: 80 }}>PR</th>
                <th style={{ width: 170 }}>Verdict</th>
                <th>Reason</th>
                <th style={{ width: 110 }}>When</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={`${r.kind}-${r.repoId}-${r.prNumber}-${r.at}`}>
                  <td>
                    <span
                      style={{
                        fontSize: 11,
                        textTransform: "uppercase",
                        letterSpacing: 0.4,
                        color: "var(--bss-fg-muted)"
                      }}
                    >
                      {r.kind}
                    </span>
                  </td>
                  <td>
                    <code className="bss-code-inline">{r.repoId}</code>
                  </td>
                  <td>
                    <a
                      href={prUrl(r)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bss-badge bss-badge--gray"
                      style={{
                        color: "var(--bss-info)",
                        textDecoration: "none"
                      }}
                    >
                      <Icon.GitPullRequest size={11} />#{r.prNumber}
                    </a>
                  </td>
                  <td>
                    <span className={verdictBadgeClass(r.verdict)}>
                      <span className="bss-dot" />
                      {verdictLabel(r.verdict)}
                    </span>
                  </td>
                  <td>
                    <span
                      style={{
                        fontSize: 12.5,
                        color: "var(--bss-fg-muted)"
                      }}
                    >
                      {r.reason || (
                        <span style={{ color: "var(--bss-fg-subtle)" }}>—</span>
                      )}
                    </span>
                  </td>
                  <td>
                    <span
                      style={{
                        fontSize: 12.5,
                        color: "var(--bss-fg-muted)"
                      }}
                    >
                      {relativeTime(r.at)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length === 0 && (
            <EmptyState
              title="No rejections in this window."
              hint="Try widening the window or clearing the search."
            />
          )}
        </div>

        <div className="bss-list-foot">
          <div>
            {total === 0
              ? "showing 0 of 0"
              : `showing ${offset + 1}–${offset + items.length} of ${total}`}
            {truncated && (
              <span
                style={{ color: "var(--bss-warning, #b7791f)", marginLeft: 8 }}
                title="More than 2000 rows match in this window. Pagination + ordering may be incomplete past that point — narrow the window for a complete view."
              >
                · capped at 2000 per source — narrow the window
              </span>
            )}
            {stale && (
              <span
                style={{ color: "var(--bss-warning, #b7791f)", marginLeft: 8 }}
                title={apiErrorMessage(error, "Last refresh failed")}
              >
                · reconnecting — showing last known data
              </span>
            )}
          </div>
          <div className="bss-pager">
            <button
              type="button"
              className="bss-btn bss-btn--ghost"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Prev
            </button>
            <button
              type="button"
              className="bss-btn bss-btn--ghost"
              disabled={offset + items.length >= total}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </>
    );

  return (
    <Shell>
      <div className="bss-page-hd">
        <div>
          <h1>Rejections</h1>
          <div className="bss-page-hd-sub">
            PRs the agent did not adopt or review, and why — so nothing gets
            silently dropped.
          </div>
        </div>
        <div className="bss-page-hd-right">
          <RefreshButton onClick={refresh} loading={loading} />
        </div>
      </div>
      {toolbar}
      {body}
    </Shell>
  );
}
